import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { loadProductPna } from "~/domains/pna/pnaCommands";
import { queryClient } from "~/app/queryClient";
import { trackPdpLoad } from "~/app/analytics/ga/ga";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { type RootState } from "~/app/store";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const PDP_SELECT_PRICE_REFERENCE = "PDP_SELECT_PRICE_REFERENCE";

function normalizePriceReference(priceReference: string) {
  return priceReference === "-" ? "" : priceReference;
}

export function loadPDPData(product: PdpPageProductData, priceReference: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const isSalesman = userIsSalesman();
    const productCode = product.code;
    dispatch(addBaseProductDataAction(product, true));

    const baseProduct = getState().products.basic[productCode];
    const normalizedPriceReference = normalizePriceReference(priceReference);

    // Initially load the product for default price reference
    const mainPnaPromise = loadProductPna(queryClient, {
      productCode: baseProduct.code,
      priceReference: normalizedPriceReference,
    });

    if (isKITProduct(baseProduct)) {
      const loadPartPna = (partProduct: ProductData) => {
        loadProductPna(queryClient, {
          productCode: partProduct.code,
          priceReference: normalizedPriceReference,
        }).catch(() => undefined);
      };

      if (baseProduct.optionalParts.length > 0) {
        baseProduct.optionalParts.forEach((product) => loadPartPna(product.product));
      }

      // Latent bug preserved verbatim: the includedParts branch iterates optionalParts.
      if (baseProduct.includedParts.length > 0) {
        baseProduct.optionalParts.forEach((product) => loadPartPna(product.product));
      }
    }

    if (!isSalesman) {
      let price;
      let availability;
      try {
        const mainPna = await mainPnaPromise;
        price = mainPna.price.length === 1 ? mainPna.price[0] : undefined;
        availability = mainPna.availability;
      } catch {
        price = undefined;
        availability = undefined;
      }

      trackPdpLoad(baseProduct, price, availability, priceReference);
    }

    dispatch(loadCartAmounts());
  };
}

export function selectPriceReference(productCode: string, priceReference: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const baseProduct = getState().products.basic[productCode];
    if (baseProduct) {
      loadProductPna(queryClient, {
        productCode: baseProduct.code,
        priceReference: normalizePriceReference(priceReference),
      }).catch(() => undefined);
    }
    dispatch(selectPriceReferenceAction(priceReference));
  };
}

function selectPriceReferenceAction(priceReference: string) {
  return {
    type: PDP_SELECT_PRICE_REFERENCE,
    payload: priceReference,
  } as const;
}

export type PdpActions = ReturnType<typeof selectPriceReferenceAction>;
